import {
    defaultCustomOnTaskRecurrenceMonthly,
    defaultOnTaskRecurrenceMonthly,
    TaskRecurrenceMonthlyPayload,
} from 'aos-services/src/services/tasks/types/payload/TaskRecurrenceMonthlyFormPayload'
import { TaskRecurrenceType } from 'aos-services/src/services/tasks/types/task/TaskRecurrenceType'
import { HStack, Radio, RadioGroup, RadioIndicator, VStack } from 'aos-ui-universal'
import React, { FC } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { NumberField } from './NumberField'
import { NumberRowField } from './NumberRowField'
import { RecurrenceCustomOnComponent } from './RecurrenceCustomOnComponent'
import { TaskRecurrenceFormProps } from './TaskRecurrenceFormProps'

export interface RecurrenceOnState {
    customOn: boolean
    regularOn: boolean
}

const REGULAR_ON = 'regular'
const CUSTOM_ON = 'custom'

export const TaskRecurrenceMonthly: FC<TaskRecurrenceMonthlyProps> = ({
    value,
    onChange,
    errors,
    canEdit,
}) => {
    const { t: translate } = useTranslation()

    if (value?.type !== TaskRecurrenceType.Monthly) {
        return null
    }

    const recurrenceErrors = errors as FieldErrors<TaskRecurrenceMonthlyPayload> | undefined
    const isCustomOn = value.customOn !== undefined && value.customOn !== null

    const changeOnMode = (mode: string) => {
        if (canEdit) {
            onChange(
                mode === REGULAR_ON
                    ? defaultOnTaskRecurrenceMonthly
                    : defaultCustomOnTaskRecurrenceMonthly,
            )
        }
    }

    return (
        <>
            <NumberRowField
                prefixLabel={translate('every', { ns: 'tasks' })}
                suffixLabel={translate('months', { ns: 'tasks' })}
                error={
                    recurrenceErrors?.every ? translate('every.error', { ns: 'tasks' }) : undefined
                }
            >
                <NumberField
                    className='w-16'
                    value={value.every}
                    onChange={every => onChange({ every })}
                    disabled={!canEdit}
                />
            </NumberRowField>
            <RadioGroup
                value={isCustomOn ? CUSTOM_ON : REGULAR_ON}
                onChange={changeOnMode}
                isDisabled={!canEdit}
            >
                <VStack className='shrink-0 gap-4'>
                    <HStack className='shrink-0 items-center gap-4'>
                        <Radio value={REGULAR_ON}>
                            <RadioIndicator />
                        </Radio>
                        <NumberRowField
                            prefixLabel={translate('on', { ns: 'tasks' })}
                            suffixLabel={translate('day', { ns: 'tasks' })}
                            error={
                                recurrenceErrors?.on
                                    ? translate('monthly.on.error', { ns: 'tasks' })
                                    : undefined
                            }
                        >
                            <NumberField
                                className='w-16'
                                value={value.on}
                                onChange={on => onChange({ on })}
                                disabled={!canEdit || isCustomOn}
                            />
                        </NumberRowField>
                    </HStack>
                    <HStack className='shrink-0 items-start gap-4'>
                        <Radio value={CUSTOM_ON} className='mt-2'>
                            <RadioIndicator />
                        </Radio>
                        <RecurrenceCustomOnComponent
                            value={value}
                            onChange={onChange}
                            errors={errors}
                            canEdit={canEdit}
                        />
                    </HStack>
                </VStack>
            </RadioGroup>
        </>
    )
}

export type TaskRecurrenceMonthlyProps = TaskRecurrenceFormProps
